import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/sections/PageHeader'
import { PageMeta } from '@/components/PageMeta'
import { Cookie } from 'lucide-react'

export function CookiePolicyPage() {
  const { t } = useTranslation('pages')

  return (
    <>
      <PageMeta title={`${t('cookies.title')} — FG Automazioni`} description={t('cookies.subtitle')} />
      <PageHeader
        title={t('cookies.title')}
        subtitle={t('cookies.subtitle')}
        tag={t('cookies.tag')}
        icon={Cookie}
        gradient="amber"
      />

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <div className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-p:text-slate-600">
            {/* Introduzione */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('cookies.introTitle')}</h2>
              <p className="text-slate-600 leading-relaxed">{t('cookies.introText')}</p>
            </div>

            {/* Cosa sono i cookie */}
            <div className="mb-12">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('cookies.whatAreTitle')}</h3>
              <p className="text-slate-600">{t('cookies.whatAreText')}</p>
            </div>

            {/* Tipologie di cookie */}
            <div className="mb-12">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('cookies.typesTitle')}</h3>

              {/* Tecnici */}
              <div className="mb-8 rounded-2xl bg-slate-50 p-6">
                <h4 className="text-lg font-semibold text-slate-900 mb-2">{t('cookies.technicalTitle')}</h4>
                <p className="text-slate-600">{t('cookies.technicalText')}</p>
                <ul className="list-disc pl-6 mt-3 space-y-1 text-slate-600">
                  <li>{t('cookies.technical.item1')}</li>
                  <li>{t('cookies.technical.item2')}</li>
                  <li>{t('cookies.technical.item3')}</li>
                </ul>
              </div>

              {/* Analitici */}
              <div className="mb-8 rounded-2xl bg-slate-50 p-6">
                <h4 className="text-lg font-semibold text-slate-900 mb-2">{t('cookies.analyticsTitle')}</h4>
                <p className="text-slate-600">{t('cookies.analyticsText')}</p>
              </div>

              {/* Profilazione */}
              <div className="mb-8 rounded-2xl bg-slate-50 p-6">
                <h4 className="text-lg font-semibold text-slate-900 mb-2">{t('cookies.profilingTitle')}</h4>
                <p className="text-slate-600">{t('cookies.profilingText')}</p>
              </div>
            </div>

            {/* Cookie utilizzati */}
            <div className="mb-12">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('cookies.usedTitle')}</h3>
              <p className="text-slate-600 mb-4">{t('cookies.usedText')}</p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-3 font-semibold text-slate-900 rounded-tl-lg">{t('cookies.table.name')}</th>
                      <th className="px-4 py-3 font-semibold text-slate-900">{t('cookies.table.type')}</th>
                      <th className="px-4 py-3 font-semibold text-slate-900">{t('cookies.table.duration')}</th>
                      <th className="px-4 py-3 font-semibold text-slate-900 rounded-tr-lg">{t('cookies.table.purpose')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <tr>
                      <td className="px-4 py-3 text-slate-600">i18next</td>
                      <td className="px-4 py-3 text-slate-600">{t('cookies.table.technical')}</td>
                      <td className="px-4 py-3 text-slate-600">{t('cookies.table.session')}</td>
                      <td className="px-4 py-3 text-slate-600">{t('cookies.table.langPurpose')}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-slate-600">cookieConsent</td>
                      <td className="px-4 py-3 text-slate-600">{t('cookies.table.technical')}</td>
                      <td className="px-4 py-3 text-slate-600">1 {t('cookies.table.year')}</td>
                      <td className="px-4 py-3 text-slate-600">{t('cookies.table.consentPurpose')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Gestione cookie */}
            <div className="mb-12">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('cookies.managementTitle')}</h3>
              <p className="text-slate-600 mb-4">{t('cookies.managementText')}</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600">
                <li>
                  <strong>Chrome:</strong> {t('cookies.management.chrome')}
                </li>
                <li>
                  <strong>Firefox:</strong> {t('cookies.management.firefox')}
                </li>
                <li>
                  <strong>Safari:</strong> {t('cookies.management.safari')}
                </li>
                <li>
                  <strong>Edge:</strong> {t('cookies.management.edge')}
                </li>
              </ul>
            </div>

            {/* Disabilitazione cookie */}
            <div className="mb-12">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('cookies.disableTitle')}</h3>
              <p className="text-slate-600">{t('cookies.disableText')}</p>
            </div>

            {/* Contatti */}
            <div className="mb-12 rounded-2xl bg-amber-50 p-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{t('cookies.contactTitle')}</h3>
              <p className="text-slate-600 mb-2">{t('cookies.contactText')}</p>
              <div className="space-y-1 text-slate-600">
                <p className="font-medium text-slate-900">FG Automazioni S.r.l.</p>
                <p>Email: info@fgautomazioni.it</p>
              </div>
            </div>

            {/* Data aggiornamento */}
            <div className="pt-8 border-t border-slate-200">
              <p className="text-sm text-slate-500">{t('cookies.lastUpdated')}: 01/04/2026</p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
